import orderBy from "lodash/orderBy";
import Spinner from "../../components/Spinner";
import { useHypertensionPatientsQuery } from "../../data/queries";
import { HypertensionPatient } from "../../model/Patient";
import { formatDate } from "../../util/date-utils.ts";
import HeaderCell from "./HeaderCell";
import PatientDataCell from "./PatientDataCell";
import PatientStatusCapsule from "./PatientStatusCapsule";
import PriorityIconWithTooltip from "./PriorityIconWithTooltip";
import { daysSinceReviewFor, formatDays, patientStatusOrder } from "./Utils.tsx";
import ViewLink from "./ViewLink";

const isCritical = (patient: HypertensionPatient) => patient.unreviewedReadings.critical > 0;

const HypertensionPopulationList = () => {
  const { isFetching, isError, data: patients = [] } = useHypertensionPatientsQuery();

  const sortedPatients = orderBy(
    patients,
    [
      (patient) => patientStatusOrder(patient.status),
      (patient) => isCritical(patient),
      (patient) => daysSinceReviewFor(patient) ?? Number.MIN_VALUE,
      (patient) => patient.lastName,
      (patient) => patient.firstName,
    ],
    ["asc", "desc", "desc", "asc", "asc"]
  );

  return (
    <>
      <div className="grid grid-cols-[auto_auto_auto_auto_1fr_auto_auto_auto_auto]">
        <HeaderCell className="pe-8 ps-4">Last name</HeaderCell>
        <HeaderCell className="pe-8">First name</HeaderCell>
        <HeaderCell className="pe-8">MRN</HeaderCell>
        <HeaderCell className="pe-8">Delivery date</HeaderCell>
        <HeaderCell className="pe-4">Protocol</HeaderCell>
        <HeaderCell className="pe-4">Journey status</HeaderCell>
        <HeaderCell className="pe-4">Days since review</HeaderCell>
        <HeaderCell className="pe-4" />
        <HeaderCell className="pe-4" />
        {sortedPatients.map((patient, i) => (
          <HypertensionPatientRow key={`patient-${patient.id}`} patient={patient} rowIndex={i} />
        ))}
      </div>
      {isFetching && patients.length === 0 && (
        <div className="flex items-center justify-center gap-2 p-4">
          <Spinner />
          <span>Loading...</span>
        </div>
      )}
      {isError ? (
        <div className="p-4 text-center italic text-red-600">
          Failed to load patients. Please try again later.
        </div>
      ) : !isFetching && sortedPatients.length === 0 ? (
        <div className="p-4 text-center italic text-primary/60">
          No active Bloomlife Connects hypertension orders. Come back soon!
        </div>
      ) : null}
    </>
  );
};

const HypertensionPatientRow = ({
  patient,
  rowIndex,
}: {
  patient: HypertensionPatient;
  rowIndex: number;
}) => {
  const daysSinceReview = daysSinceReviewFor(patient);
  const isMonitoring = ["monitoring", "postpartum-monitoring"].includes(patient.status);
  const deliveryDateIsEstimate = patient.estimatedDateOfDelivery && !patient.deliveryDate;
  const deliveryDate = patient.deliveryDate ?? patient.estimatedDateOfDelivery;

  return (
    <>
      <PatientDataCell
        rowIndex={rowIndex}
        title={patient.lastName}
        className="max-w-[20ch] pe-12 ps-4"
      >
        {patient.lastName}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} title={patient.firstName} className="max-w-[30ch] pe-12">
        {patient.firstName}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-12">
        {patient.medicalRecordNumber}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-12">
        {deliveryDate
          ? `${formatDate("MM/dd/yy", deliveryDate)} ${deliveryDateIsEstimate ? "EDD" : ""}`
          : "-"}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="overflow-hidden pe-4">
        <span className="truncate" title={patient.personalizedClinicalProtocol.name}>
          {patient.personalizedClinicalProtocol.name ?? "-"}
        </span>
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        <PatientStatusCapsule status={patient.status} />
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        {isMonitoring && daysSinceReview ? formatDays(Math.round(daysSinceReview)) : "-"}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        {isCritical(patient) && (
          <PriorityIconWithTooltip
            arrow="double"
            arrowDirection="up"
            color="text-measurement-text-emergency"
            tooltipText="Critical high blood pressure"
          />
        )}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        <ViewLink to={`/patients/${patient.id}/hypertension`} />
      </PatientDataCell>
    </>
  );
};

export default HypertensionPopulationList;
